<script>
export default {
    name: 'BaseInput',
};
</script>

<script setup>
import { computed, onMounted, ref, useSlots } from 'vue';
import { SIZE, TYPE } from '@/components/base-components/components/input/index.js';

const props = defineProps({
    value: {
        type: [String, Number],
        default: '',
    },
    name: {
        type: String,
    },
    placeholder: {
        type: String,
    },
    type: {
        type: String,
        default: TYPE.DEFAULT,
        validator: value => Object.values(TYPE).includes(value),
    },
    nativeType: {
        type: String,
        default: 'text',
        validator: value => ['text', 'textarea', 'email', 'password', 'hidden'].includes(value),
    },
    size: {
        type: String,
        default: SIZE.MEDIUM,
        validator: value => Object.values(SIZE).includes(value),
    },
    nativeSize: {
        type: [String, Number],
        default: 20,
    },
    minlength: {
        type: [String, Number],
    },
    maxlength: {
        type: [String, Number],
    },
    center: {
        type: Boolean,
    },
    disabled: {
        type: Boolean,
    },
    clearable: {
        type: Boolean,
    },
    readonly: {
        type: Boolean,
    },
    autocomplete: {
        type: String,
    },
    showPassword: {
        type: Boolean,
    },
    showLimit: {
        type: Boolean,
    },
    autofocus: {
        type: Boolean,
    },
    rows: {
        type: [String, Number],
        default: 2,
    },
    resize: {
        type: String,
        validator: value => ['none', 'both', 'horizontal', 'vertical', 'auto'].includes(value),
    },
    inputClass: {
        type: [String, Array, Object],
    },
    wrapperClass: {
        type: [String, Array, Object],
    },
    prependClass: {
        type: [String, Array, Object],
    },
    appendClass: {
        type: [String, Array, Object],
    },
});

const emit = defineEmits({
    input: null,
    focus: null,
    blur: null,
    change: null,
    keydown: null,
    clear: null,
    mouseover: null,
    mouseleave: null,
});

const model = computed({
    get: () => props.value,
    set: value => emit('input', value),
});

const isEmptyModel = computed(() => model.value === null
    || model.value === undefined
    || String(model.value) === '');

const inputRef = ref(null);
const textareaRef = ref(null);

const input = computed(() => inputRef.value || textareaRef.value);

const focused = ref(false);

function handleFocus(event) {
    focused.value = true;
    emit('focus', event);
}

function handleBlur(event) {
    focused.value = false;
    emit('blur', event);
}

function handleChange(event) {
    emit('change', event.target.value);
}

function handleKeydown(event) {
    emit('keydown', event);
}

function handleClear() {
    emit('clear');
    emit('change', '');
    model.value = '';
}

const hovering = ref(false);

function handleMouseover(event) {
    hovering.value = true;
    emit('mouseover', event);
}

function handleMouseleave(event) {
    hovering.value = false;
    emit('mouseleave', event);
}

function handleMousedown() {
}

const passwordVisible = ref(false);

function handlePasswordVisible() {
    passwordVisible.value = !passwordVisible.value;
}

function focus(options) {
    input.value?.focus(options);
}

function select() {
    input.value?.select();
}

function blur() {
    input.value?.blur();
}

defineExpose({
    focus,
    select,
    blur,
    input,
});

onMounted(() => {
    if (props.autofocus) {
        focus({ preventScroll: true });
    }
});

const isTextarea = computed(() => props.nativeType === 'textarea');

const slots = useSlots();

const showClear = computed(() => props.clearable
    && !isEmptyModel.value
    && !props.readonly
    && !props.disabled);

const showPasswordVisible = computed(() => props.showPassword
    && !isEmptyModel.value
    && !props.readonly
    && !props.disabled);

const showLimitation = computed(() => props.showLimit
    && (!!props.minlength || !!props.maxlength)
    && !props.readonly
    && !props.disabled);

const showSuffix = computed(() => slots.suffix
    || showClear.value
    || showPasswordVisible.value
    || showLimitation.value);

const internalNativeType = computed(() => {
    if (props.nativeType === 'password' && props.showPassword) {
        return passwordVisible.value ? 'text' : 'password';
    }

    return props.nativeType;
});

const limit = computed(() => {
    if (props.minlength && length.value < props.minlength) return props.minlength;
    if (props.maxlength && length.value < props.maxlength) return props.maxlength;

    return props.maxlength || props.minlength;
});

const length = computed(() => {
    if (isEmptyModel.value) return 0;

    return [...model.value].length;
});

const minLengthNotValid = computed(() => !isEmptyModel.value
    && props.minlength
    && length.value < props.minlength);

const internalType = computed(() => {
    if (minLengthNotValid.value) return 'danger';

    return props.type;
});

const textClass = computed(() => ({
    [SIZE.SMALL]: '-my-px text-xs leading-7',
    [SIZE.MEDIUM]: '-my-px text-sm leading-8',
    [SIZE.LARGE]: '-my-px text-sm leading-10',
}[props.size]));

const placeholderClass = computed(() => ({
    [TYPE.DEFAULT]: 'placeholder-gray-500/40',
    [TYPE.PRIMARY]: 'placeholder-primary-600/40',
    [TYPE.SECONDARY]: 'placeholder-secondary-600/40',
    [TYPE.PRIMARY_OLD]: 'placeholder-science-blue-700/40',
    [TYPE.SECONDARY_OLD]: 'placeholder-science-blue-800/40',
    [TYPE.SUCCESS]: 'placeholder-emerald-700/40',
    [TYPE.WARNING]: 'placeholder-orange-600/40',
    [TYPE.DANGER]: 'placeholder-red-700/40',
    [TYPE.INFO]: 'placeholder-gray-500/40',
}[props.type]));

const paddingClass = computed(() => ({
    [SIZE.SMALL]: 'px-1.5',
    [SIZE.MEDIUM]: 'px-2',
    [SIZE.LARGE]: 'px-3',
}[props.size]));

const heightClass = computed(() => ({
    [SIZE.SMALL]: 'h-7',
    [SIZE.MEDIUM]: 'h-8',
    [SIZE.LARGE]: 'h-10',
}[props.size]));

const typeClass = computed(() => ({
    [TYPE.DEFAULT]: 'text-gray-500',
    [TYPE.PRIMARY]: 'text-primary-600',
    [TYPE.SECONDARY]: 'text-secondary-950',
    [TYPE.PRIMARY_OLD]: 'text-science-blue-700',
    [TYPE.SECONDARY_OLD]: 'text-science-blue-800',
    [TYPE.SUCCESS]: 'text-emerald-700',
    [TYPE.WARNING]: 'text-orange-600',
    [TYPE.DANGER]: 'text-red-700',
    [TYPE.INFO]: 'text-gray-500',
}[internalType.value]));

const typeOpacityClass = computed(() => ({
    [TYPE.DEFAULT]: 'text-gray-500/75',
    [TYPE.PRIMARY]: 'text-primary-600/75',
    [TYPE.SECONDARY]: 'text-secondary-950/75',
    [TYPE.PRIMARY_OLD]: 'text-science-blue-700/75',
    [TYPE.SECONDARY_OLD]: 'text-science-blue-800/75',
    [TYPE.SUCCESS]: 'text-emerald-700/75',
    [TYPE.WARNING]: 'text-orange-600/75',
    [TYPE.DANGER]: 'text-red-700/75',
    [TYPE.INFO]: 'text-gray-500/75',
}[internalType.value]));

const borderClass = computed(() => {
    if (focused.value) {
        return {
            [TYPE.DEFAULT]: 'border-science-blue-800',
            [TYPE.PRIMARY]: 'border-primary-600',
            [TYPE.SECONDARY]: 'border-secondary-950',
            [TYPE.PRIMARY_OLD]: 'border-science-blue-500',
            [TYPE.SECONDARY_OLD]: 'border-science-blue-700',
            [TYPE.SUCCESS]: 'border-emerald-600',
            [TYPE.WARNING]: 'border-orange-500',
            [TYPE.DANGER]: 'border-red-600',
            [TYPE.INFO]: 'border-gray-500',
        }[internalType.value];
    }

    return {
        [TYPE.DEFAULT]: 'border-gray-400/40 hover:border-gray-400/80',
        [TYPE.PRIMARY]: 'border-primary-600/40 hover:border-primary-600/80',
        [TYPE.SECONDARY]: 'border-secondary-950/40 hover:border-secondary-950/80',
        [TYPE.PRIMARY_OLD]: 'border-science-blue-500/40 hover:border-science-blue-500/80',
        [TYPE.SECONDARY_OLD]: 'border-science-blue-700/40 hover:border-science-blue-700/80',
        [TYPE.SUCCESS]: 'border-emerald-600/40 hover:border-emerald-600/80',
        [TYPE.WARNING]: 'border-orange-500/40 hover:border-orange-500/80',
        [TYPE.DANGER]: 'border-red-600/40 hover:border-red-600/80',
        [TYPE.INFO]: 'border-gray-500/40 hover:border-gray-500/80',
    }[internalType.value];
});

const backgroundClass = computed(() => {
    if (!props.disabled) return 'bg-white';

    return {
        [TYPE.DEFAULT]: 'bg-gray-500/5 opacity-75',
        [TYPE.PRIMARY]: 'bg-primary-600/5 opacity-75',
        [TYPE.SECONDARY]: 'bg-secondary-950/5 opacity-75',
        [TYPE.PRIMARY_OLD]: 'bg-science-blue-700/5 opacity-75',
        [TYPE.SECONDARY_OLD]: 'bg-science-blue-800/5 opacity-75',
        [TYPE.SUCCESS]: 'bg-emerald-700/5 opacity-75',
        [TYPE.WARNING]: 'bg-orange-600/5 opacity-75',
        [TYPE.DANGER]: 'bg-red-700/5 opacity-75',
        [TYPE.INFO]: 'bg-gray-500/5 opacity-75',
    }[internalType.value];
});

const slotClass = computed(() => [
    'flex items-center border',
    typeOpacityClass.value,
    paddingClass.value,
    {
        [TYPE.DEFAULT]: 'border-gray-400/40 bg-gray-500/5',
        [TYPE.PRIMARY]: 'border-primary-600/40 bg-primary-600/5',
        [TYPE.SECONDARY]: 'border-secondary-950/40 bg-secondary-950/5',
        [TYPE.PRIMARY_OLD]: 'border-science-blue-500/40 bg-science-blue-700/5',
        [TYPE.SECONDARY_OLD]: 'border-science-blue-700/40 bg-science-blue-800/5',
        [TYPE.SUCCESS]: 'border-emerald-600/40 bg-emerald-700/5',
        [TYPE.WARNING]: 'border-orange-500/40 bg-orange-600/5',
        [TYPE.DANGER]: 'border-red-600/40 bg-red-700/5',
        [TYPE.INFO]: 'border-gray-500/40 bg-gray-500/5',
    }[internalType.value],
]);

const textareaTextClass = computed(() => ({
    [SIZE.SMALL]: '-my-px text-xs py-1.5',
    [SIZE.MEDIUM]: '-my-px text-sm py-1.5',
    [SIZE.LARGE]: '-my-px text-sm py-2.5',
}[props.size]));

const resizeClass = computed(() => ({
    none: 'resize-none',
    both: 'resize',
    horizontal: 'resize-x',
    vertical: 'resize-y',
}[props.resize] ?? 'resize-none'));
</script>

<template>
    <div
        v-show="nativeType !== 'hidden'"
        class="group inline-flex"
        :class="{
            'cursor-not-allowed': disabled,
        }"
        @mouseover="handleMouseover"
        @mouseleave="handleMouseleave"
        @click.stop.prevent=""
    >
        <template v-if="isTextarea">
            <div
                class="relative flex-1 self-start overflow-hidden rounded-sm border transition-colors focus:outline-hidden"
                :class="[
                    resize === 'auto' ? 'grid' : 'flex items-center',
                    wrapperClass || [typeClass, borderClass, backgroundClass,],
                    {
                        'pointer-events-none': disabled,
                    }
                ]"
            >
                <textarea
                    v-model="model"
                    ref="textareaRef"
                    class="grow border-none bg-transparent outline-hidden focus:outline-hidden"
                    :class="[
                        resizeClass,
                        placeholderClass,
                        inputClass || [textareaTextClass, paddingClass],
                        {
                            'pointer-events-none select-none': disabled,
                            'text-center': center,
                            'col-start-1 row-start-1 w-full overflow-hidden break-words whitespace-pre-wrap': resize === 'auto',
                        }
                    ]"
                    :name="name"
                    :placeholder="placeholder"
                    :minlength="minlength"
                    :maxlength="maxlength"
                    :readonly="readonly"
                    :autocomplete="autocomplete"
                    :disabled="disabled"
                    :rows="rows"
                    @focus="handleFocus"
                    @blur="handleBlur"
                    @change="handleChange"
                    @keydown="handleKeydown"
                />

                <div
                    v-if="resize === 'auto'"
                    v-text="`${model} `"
                    aria-hidden="true"
                    class="pointer-events-none invisible col-start-1 row-start-1 w-full grow break-words whitespace-pre-wrap text-red-500"
                    :class="[
                        inputClass || [textareaTextClass, paddingClass],
                    ]"
                />

                <span
                    v-if="showLimitation"
                    class="absolute right-2 bottom-0.5 rounded-sm px-1 py-0.5 text-xs whitespace-nowrap"
                    :class="[
                        typeOpacityClass,
                        backgroundClass,
                    ]"
                >
                    {{ length }} / {{ limit }}
                </span>
            </div>
        </template>
        <template v-else>
            <div
                v-if="$slots.prepend"
                :class="[
                    prependClass || ['rounded-l border-r-0', slotClass],
                    heightClass
                ]"
            >
                <slot name="prepend" />
            </div>
            <div
                class="relative flex flex-1 items-center self-start overflow-hidden border transition-colors focus:outline-hidden"
                :class="[
                    wrapperClass || [typeClass, borderClass, backgroundClass,],
                    {
                        'rounded-l': !$slots.prepend,
                        'rounded-r': !$slots.append,
                        'pointer-events-none': disabled,
                    }
                ]"
            >
                <span
                    v-if="$slots.prefix"
                    class="inline-flex items-center gap-x-2"
                    :class="paddingClass"
                >
                    <slot name="prefix" />
                </span>

                <input
                    v-model="model"
                    class="w-full border-none bg-transparent outline-hidden focus:outline-hidden"
                    ref="inputRef"
                    :class="[
                        inputClass || [paddingClass,],
                        textClass,
                        heightClass,
                        placeholderClass,
                        {
                            'pointer-events-none select-none': disabled,
                            'text-center': center,
                            'pl-0': $slots.prefix,
                            'pr-0': showSuffix,
                        }
                    ]"
                    :type="internalNativeType"
                    :name="name"
                    :placeholder="placeholder"
                    :minlength="minlength"
                    :maxlength="maxlength"
                    :readonly="readonly"
                    :autocomplete="autocomplete"
                    :disabled="disabled"
                    :size="nativeSize"
                    @focus="handleFocus"
                    @blur="handleBlur"
                    @change="handleChange"
                    @keydown="handleKeydown"
                >

                <span
                    v-if="showSuffix"
                    class="inline-flex shrink-0 items-center gap-x-2"
                    :class="paddingClass"
                >
                    <template v-if="$slots.suffix">
                        <slot name="suffix" />
                    </template>

                    <span
                        v-if="showClear"
                        class="inline-flex cursor-pointer items-center justify-end opacity-0 transition-opacity hover:opacity-100"
                        :class="{
                            'opacity-50': focused || hovering,
                        }"
                        @mousedown.prevent="handleMousedown"
                        @click.prevent="handleClear"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="h-4 w-4"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M12 9.75L14.25 12m0 0l2.25 2.25M14.25 12l2.25-2.25M14.25 12L12 14.25m-2.58 4.92l-6.375-6.375a1.125 1.125 0 010-1.59L9.42 4.83c.211-.211.498-.33.796-.33H19.5a2.25 2.25 0 012.25 2.25v10.5a2.25 2.25 0 01-2.25 2.25h-9.284c-.298 0-.585-.119-.796-.33z"
                            />
                        </svg>
                    </span>

                    <span
                        v-if="showPasswordVisible"
                        class="inline-flex cursor-pointer items-center justify-end opacity-0 transition-opacity hover:opacity-100"
                        :class="{
                            'opacity-50': !isEmptyModel || focused || hovering,
                        }"
                        @mousedown.prevent="handleMousedown"
                        @click="handlePasswordVisible"
                    >
                        <svg
                            v-if="passwordVisible"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="h-4 w-4"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                            />
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                        </svg>
                        <svg
                            v-else
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="h-4 w-4"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                            />
                        </svg>
                    </span>

                    <span
                        v-if="showLimitation"
                        class="text-xs whitespace-nowrap"
                        :class="typeOpacityClass"
                    >
                        {{ length }} / {{ limit }}
                    </span>
                </span>
            </div>
            <div
                v-if="$slots.append"
                :class="[
                    appendClass || ['rounded-r border-l-0', slotClass],
                    heightClass
                ]"
            >
                <slot name="append" />
            </div>
        </template>
    </div>
</template>
